
import { supabase } from '../supabase';
import { User, Conversation, Message, UserRole } from '../../types';

export const getConversations = async (user: User): Promise<Conversation[]> => {
    let query = supabase.from('conversations').select(`
        *,
        teacher:teacher_id(name),
        admin:admin_id(name),
        student:student_id(name),
        messages:messages(count)
    `);

    if (user.role === UserRole.TEACHER) {
        // Teacher sees:
        // 1. Chats with Admin (where they are teacher_id AND student_id is NULL)
        // 2. Chats with Students (where they are teacher_id AND student_id is NOT NULL)
        query = query.eq('teacher_id', user.id);
    } else if (user.role === UserRole.STUDENT) {
        // Student sees chats where they are the student_id
        query = query.eq('student_id', user.id);
    } else if (user.role === UserRole.ADMIN) {
        // Admin sees all support chats (where student_id is NULL).
        // This filters out internal chats between Teacher <-> Student.
        query = query.is('student_id', null); 
    } else {
        return [];
    }

    const { data, error } = await query;
    if (error) throw error;

    const convs = await Promise.all(data.map(async (c: any) => {
        const { count } = await supabase
            .from('messages')
            .select('*', { count: 'exact', head: true })
            .eq('conversation_id', c.id)
            .eq('is_read', false)
            .neq('sender_id', user.id);

        // Determine the "Other Party" name based on who is viewing
        let otherName = 'Okänd';
        
        if (user.role === UserRole.STUDENT) {
            otherName = c.teacher?.name || 'Min Lärare';
        } else if (user.role === UserRole.TEACHER) {
            // If it's a student chat, show student name. If support chat, show Admin/Support.
            if (c.student_id) {
                otherName = c.student?.name || 'Elev';
            } else {
                otherName = c.admin?.name || 'Support';
            }
        } else if (user.role === UserRole.ADMIN) {
            otherName = c.teacher?.name || 'Lärare';
        }

        return {
            id: c.id,
            teacherId: c.teacher_id,
            adminId: c.admin_id,
            studentId: c.student_id, // Add this to type definition if needed or just use internally
            teacherName: c.teacher?.name,
            adminName: c.admin?.name,
            studentName: c.student?.name,
            displayName: otherName, // Helper for UI
            subject: c.subject,
            updatedAt: new Date(c.updated_at).getTime(),
            unreadCount: count || 0
        };
    }));

    return convs.sort((a, b) => b.updatedAt - a.updatedAt);
};

export const getMessages = async (conversationId: string): Promise<Message[]> => {
    const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });
    
    if (error) throw error;

    return (data || []).map(m => ({
        id: m.id,
        conversationId: m.conversation_id,
        senderId: m.sender_id,
        content: m.content,
        createdAt: new Date(m.created_at).getTime(),
        isRead: m.is_read
    }));
};

export const sendMessage = async (conversationId: string, senderId: string, content: string, isAdmin: boolean) => {
    const { error } = await supabase.from('messages').insert([{
        conversation_id: conversationId,
        sender_id: senderId,
        content
    }]);
    if (error) throw error;

    await supabase.from('conversations').update({
        updated_at: new Date().toISOString()
    }).eq('id', conversationId);
};

export const createConversation = async (
    teacherId: string, 
    adminId: string | null, 
    subject: string, 
    initialMessage: string, 
    senderId: string,
    studentId: string | null = null
): Promise<string> => {
    const { data: conv, error: convError } = await supabase.from('conversations').insert([{
        teacher_id: teacherId,
        admin_id: adminId,
        student_id: studentId,
        subject
    }]).select().single();
    
    if (convError) throw convError;

    await sendMessage(conv.id, senderId, initialMessage, false);
    return conv.id;
};

export const markMessagesAsRead = async (conversationId: string, userId: string) => {
    await supabase.from('messages')
        .update({ is_read: true })
        .eq('conversation_id', conversationId)
        .neq('sender_id', userId);
};

export const getUnreadTotal = async (user: User): Promise<number> => {
    // 1. Get my conversation IDs
    let convQuery = supabase.from('conversations').select('id');
    
    if (user.role === UserRole.TEACHER) {
        convQuery = convQuery.eq('teacher_id', user.id);
    } else if (user.role === UserRole.STUDENT) {
        convQuery = convQuery.eq('student_id', user.id);
    } else if (user.role === UserRole.ADMIN) {
        // IMPORTANT: Admins only care about support chats (no student_id)
        convQuery = convQuery.is('student_id', null);
    }
    
    const { data: convs, error } = await convQuery;
    if (error || !convs) return 0;
    
    const convIds = convs.map(c => c.id);
    if (convIds.length === 0) return 0;

    // 2. Count unread messages in those conversations sent by others
    const { count } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .in('conversation_id', convIds)
        .eq('is_read', false)
        .neq('sender_id', user.id);
        
    return count || 0;
};
